'use client';

import { c as _c } from "react/compiler-runtime";
import type { ComponentProps } from 'react';
import { memo } from 'react';
import { PrV2Description } from '../header/pr-v2-description';
import { PrV2DemoCard } from './pr-v2-demo-card';
import { usePrV2Demo } from '@/data/v2/pr/use-pr-v2-demo';
const MemoPrV2Description = memo(PrV2Description);
type PrV2DescriptionWithDemoProps = Omit<ComponentProps<typeof PrV2Description>, 'hideDemoVideoBlock'> & {
  installationId: number | null;
  /** The head commit the page shows, which the demo card's re-record names. */
  headSha: string | null;
  /** Members may read the demo session; public viewers keep the description's demo block. */
  showsDemo: boolean;
};

/**
 * The description with the demo cubic filmed above it. One read of the demo feeds both, so the
 * description leaves its demo block out exactly when the card plays that demo, and while access
 * resolves or the demo loads, so a demo the card is about to show never appears twice.
 */
export function PrV2DescriptionWithDemo(t0) {
  const $ = _c(20);
  let descriptionProps;
  let headSha;
  let installationId;
  let showsDemo;
  if ($[0] !== t0) {
    ({
      installationId,
      headSha,
      showsDemo,
      ...descriptionProps
    } = t0);
    $[0] = t0;
    $[1] = descriptionProps;
    $[2] = headSha;
    $[3] = installationId;
    $[4] = showsDemo;
  } else {
    descriptionProps = $[1];
    headSha = $[2];
    installationId = $[3];
    showsDemo = $[4];
  }
  const {
    owner,
    repo,
    number
  } = descriptionProps;
  let t1;
  if ($[5] !== installationId || $[6] !== showsDemo) {
    t1 = {
      installationId,
      enabled: showsDemo
    };
    $[5] = installationId;
    $[6] = showsDemo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const {
    data,
    isAccessLoading,
    isLoading
  } = usePrV2Demo(owner, repo, number, t1);
  const demo = showsDemo ? data?.demo ?? null : null;
  let t2;
  if ($[8] !== demo || $[9] !== headSha || $[10] !== number || $[11] !== owner || $[12] !== repo) {
    t2 = demo ? <PrV2DemoCard demo={demo} pullRequest={{
      owner,
      repo,
      number,
      headSha
    }} /> : null;
    $[8] = demo;
    $[9] = headSha;
    $[10] = number;
    $[11] = owner;
    $[12] = repo;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const t3 = showsDemo && (isAccessLoading || isLoading || demo?.state === "ready");
  let t4;
  if ($[14] !== descriptionProps || $[15] !== t3) {
    t4 = <MemoPrV2Description {...descriptionProps} hideDemoVideoBlock={t3} />;
    $[14] = descriptionProps;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t2 || $[18] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}